import React from "react";
import { useCountry } from "../contexts/CountryContext";

const S = ({ title, children }) => (
  <section style={{ marginBottom: '28px' }}>
    <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '10px' }}>{title}</h2>
    <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', lineHeight: 1.8 }}>{children}</div>
  </section>
);

const TermsOfServicePage = () => {
  const { country } = useCountry();
  return (
  <div className="page" data-testid="terms-page" style={{ maxWidth: '800px', margin: '0 auto' }}>
    <div className="page-header">
      <h1>Terms of Service</h1>
      <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Last updated: April 2026</p>
    </div>

    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '32px' }}>
      <p style={{ fontSize: '0.9375rem', color: 'var(--text-dark)', lineHeight: 1.7, marginBottom: '28px' }}>
        By accessing or using HandyHub, you agree to be bound by these Terms of Service. Please read them carefully before using the platform.
      </p>

      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 'var(--radius-md)', padding: '16px 18px', marginBottom: '28px' }}>
        <p style={{ fontSize: '0.875rem', color: '#92400e', lineHeight: 1.65, margin: 0, fontStyle: 'italic' }}>
          HandyHub reviews provider information during onboarding, however customers should independently assess providers before engaging services.
        </p>
      </div>

      <S title="1. Platform Role">
        <p>HandyHub is an online marketplace that connects customers seeking services with independent service providers ("Providers").</p>
        <p>HandyHub is not a service provider and does not perform any services listed on the platform. We facilitate introductions and provide tools for quotes, communication, and reviews.</p>
        <p>All service agreements, including pricing, scheduling, and delivery of services, are made directly between customers and providers.</p>
      </S>

      <S title="2. Independent Contractor Status">
        <p>Providers operate as independent contractors and are not employees, agents, or representatives of HandyHub.</p>
        <p>HandyHub does not control, supervise, or direct the work performed by providers and is not responsible for the quality, safety, or legality of services offered.</p>
      </S>

      <S title="3. User Accounts">
        <p>You must create an account to use certain features of HandyHub.</p>
        <p>You are responsible for:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Maintaining the confidentiality of your account credentials</li>
          <li>All activity that occurs under your account</li>
        </ul>
        <p>You must provide accurate, current, and complete information during registration.</p>
      </S>

      <S title="4. Provider Responsibilities">
        <p>Providers agree to:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Provide accurate business and qualification information</li>
          <li>Maintain all required licences and insurance for their trade</li>
          <li>Respond to customer enquiries professionally and in a timely manner</li>
          <li>Deliver services to the standard described in their quotes</li>
          <li>Comply with all applicable laws and regulations</li>
        </ul>
      </S>

      <S title="5. Customer Responsibilities">
        <p>Customers agree to:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Provide accurate job descriptions and contact information</li>
          <li>Communicate honestly with providers</li>
          <li>Pay providers as agreed for completed work</li>
          <li>Leave fair and honest reviews based on genuine experiences</li>
        </ul>
      </S>

      <S title="6. Quotes, Bookings, and Payments">
        <p>Quotes provided through HandyHub are estimates issued by independent providers. Final pricing may vary depending on the actual scope of work.</p>
        <p>HandyHub:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Does not guarantee pricing, availability, or service quality</li>
          <li>Is not responsible for payments between customers and providers</li>
        </ul>
        <p>All agreements regarding pricing, scheduling, payments, and scope are strictly between the customer and the provider.</p>
      </S>

      <S title="7. Subscriptions and Billing">
        <p>Providers access the platform through paid subscription plans.</p>
        <p>By subscribing, you agree to:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Recurring billing based on your selected plan</li>
          <li>Payment of all applicable fees on time</li>
        </ul>
        <p>Subscriptions may be cancelled at any time. Access will continue until the end of the billing period. Refunds, if any, are governed by the Cancellation Policy.</p>
        <p>HandyHub may update pricing with reasonable notice.</p>
      </S>

      <S title="8. Reviews and Content">
        <p>Users may post reviews, ratings, and other content on HandyHub.</p>
        <p>You:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Retain ownership of your content</li>
          <li>Grant HandyHub a licence to display and use it on the platform</li>
        </ul>
        <p>Content must:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Be honest and based on genuine experience</li>
          <li>Not be defamatory, abusive, or misleading</li>
        </ul>
        <p>HandyHub reserves the right to remove content that violates these guidelines.</p>
      </S>

      <S title="9. Prohibited Conduct">
        <p>You agree not to:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Use the platform for unlawful purposes</li>
          <li>Post false, misleading, or fraudulent information</li>
          <li>Harass, abuse, or threaten other users</li>
          <li>Attempt to bypass or circumvent platform systems or fees</li>
          <li>Scrape, crawl, or extract platform data without permission</li>
          <li>Impersonate any person or entity</li>
        </ul>
      </S>

      <S title="10. Disputes Between Users">
        <p>Any dispute arising from services is between the customer and the provider.</p>
        <p>HandyHub:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Is not a party to these agreements</li>
          <li>Does not guarantee resolution of disputes</li>
        </ul>
        <p>We may, at our discretion, assist with communication but are not responsible for outcomes, refunds, or enforcement.</p>
      </S>

      <S title="11. Limitation of Liability">
        <p>HandyHub provides the platform on an "as is" and "as available" basis.</p>
        <p>To the maximum extent permitted by law, HandyHub shall not be liable for:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Any loss, damage, or dispute arising from services provided by providers</li>
          <li>Indirect, incidental, special, or consequential damages</li>
        </ul>
        <p>Users acknowledge that use of the platform is at their own risk.</p>
      </S>

      <S title="12. Termination and Suspension">
        <p>HandyHub may suspend or terminate accounts that:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Violate these Terms</li>
          <li>Engage in fraudulent or harmful activity</li>
          <li>Negatively impact the platform or other users</li>
        </ul>
        <p>Users may close their accounts at any time by contacting support.</p>
      </S>

      <S title="13. Changes to These Terms">
        <p>We may update these Terms from time to time.</p>
        <p>Material changes will be communicated via email or through the platform. Continued use of HandyHub after updates constitutes acceptance of the revised Terms.</p>
      </S>

      <S title="14. Contact">
        <p>For questions about these Terms, please contact: <a href={`mailto:${country.supportEmail}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }} data-testid="terms-support-email">{country.supportEmail}</a></p>
      </S>
    </div>
  </div>
  );
};

export default TermsOfServicePage;
